const VMAGIC_DECK_BUILDER = 'vmagic_deck_builder';

/**
 * The Deck Builder screen state that survives a tab or browser close.
 *
 * Only the user's own inputs and view choices are kept. Everything else on that
 * screen — the commander, the deck's cards, their CardKingdom prices — is
 * re-derived from these two text fields by the effects in DeckBuilder.js, so it
 * comes back on its own. The fetched EDHREC/Spellbook lists are deliberately not
 * stored: they carry full card objects with every price row, which runs to
 * several MB and would not fit the localStorage budget this origin already
 * shares with the shopping cart.
 */
const DEFAULT_STATE = {
  format: 'commander',
  commanderName: '',
  deckListText: '',
  deckDescription: '',
  chatMessages: [],
  activeTabIndex: 0,
  showCards: true,
  onlyCollectionRecommendations: false,
  onlyCollectionCombos: false,
};

// The deck formats the app supports. Validated as an enum rather than added to
// TEXT_FIELDS: an arbitrary stored string would reach the server and select no card
// pool at all.
const FORMATS = ['commander', 'brawl'];

// Keep in step with the TABS array in DeckBuilder.js.
const TAB_COUNT = 5;

// Everything lives under one key, so an unbounded chat transcript could push the
// payload past the quota — and saveState swallowing that error would mean nothing
// persists at all, decklist included. Only the stored slice is capped; the
// in-memory conversation is left whole.
const MAX_STORED_MESSAGES = 60;

const CHAT_ROLES = ['user', 'model'];

// Free-text fields that must always come back as strings — see getState.
const TEXT_FIELDS = ['commanderName', 'deckListText', 'deckDescription'];

// ChatAdvisor maps over this while rendering, so a hand-edited or half-written
// value must not be able to take the page down.
function sanitizeMessages(messages) {
  if (!Array.isArray(messages)) return [];
  return messages.filter(
    message =>
      message &&
      typeof message === 'object' &&
      typeof message.content === 'string' &&
      CHAT_ROLES.indexOf(message.role) !== -1,
  );
}

function getState() {
  try {
    const stored = localStorage.getItem(VMAGIC_DECK_BUILDER);
    // Spread over the defaults so a payload written by an older version still
    // yields every field.
    const state = { ...DEFAULT_STATE, ...(JSON.parse(stored) || {}) };
    // A tab index written before the tab list changed points at a different tab
    // now, and a garbage value would select none at all.
    const tabIndex = Number(state.activeTabIndex);
    state.activeTabIndex =
      Number.isInteger(tabIndex) && tabIndex >= 0 && tabIndex < TAB_COUNT
        ? tabIndex
        : 0;
    if (FORMATS.indexOf(state.format) === -1) state.format = 'commander';
    state.chatMessages = sanitizeMessages(state.chatMessages);
    // The spread above lets a hand-edited payload replace a default with a non-string,
    // and DeckBuilder calls .trim() on all three while rendering — which would throw and
    // take the page down. Same reasoning as sanitizeMessages.
    TEXT_FIELDS.forEach(field => {
      if (typeof state[field] !== 'string') state[field] = '';
    });
    return state;
  } catch (e) {
    // This runs during the first render, so a corrupt value must not take the
    // page down with it.
    return { ...DEFAULT_STATE };
  }
}

function saveState(state) {
  try {
    const messages = sanitizeMessages(state.chatMessages);
    localStorage.setItem(
      VMAGIC_DECK_BUILDER,
      JSON.stringify({
        ...state,
        chatMessages: messages.slice(-MAX_STORED_MESSAGES),
      }),
    );
  } catch (e) {
    // Out of quota (the origin is shared with the shopping cart): degrade to
    // "not persisted" rather than throwing out of a render.
  }
}

function clearState() {
  try {
    localStorage.removeItem(VMAGIC_DECK_BUILDER);
  } catch (e) {}
}

const deckBuilderStorage = {
  getState,
  saveState,
  clearState,
};

export default deckBuilderStorage;
